import type { paths } from '@stable/contracts';
import { newIdempotencyKey } from './utils';
import { session, type Tokens } from './session';

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }

  /** class-validator messages ("name must be longer…") grouped by the field they start with. */
  fieldErrors(): Record<string, string> {
    const issues = (this.details.issues as string[] | undefined) ?? [];
    const out: Record<string, string> = {};
    for (const issue of issues) {
      const field = issue.split(' ')[0].split('.').pop() ?? '';
      if (field && !out[field]) out[field] = issue;
    }
    return out;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Adds an Idempotency-Key header (required by payment and booking POSTs). */
  idempotent?: boolean;
  /** Return the raw Response (files: PDFs, QR codes, calendar files). */
  raw?: boolean;
  /** Override the tenant (platform console calls send none). */
  tenant?: string | null;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const tokens = session.get().tokens;
  if (!tokens) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: tokens.refreshToken }) });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken: string; refreshToken: string; expiresIn: number };
      session.set({ tokens: toTokens(body) });
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

export function toTokens(body: { accessToken: string; refreshToken: string; expiresIn: number }): Tokens {
  return { accessToken: body.accessToken, refreshToken: body.refreshToken, expiresAt: Date.now() + body.expiresIn * 1000 };
}

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path.startsWith('/') ? path : `/v1/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Fill `{id}` style placeholders. */
export function fillPath(path: string, params: Record<string, string>): string {
  return path.replace(/\{(\w+)\}/g, (_, k: string) => encodeURIComponent(params[k] ?? ''));
}

export async function request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const doFetch = async () => {
    const { tokens, tenant } = session.get();
    const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
    const t = opts.tenant === undefined ? tenant : opts.tenant;
    if (t) headers['X-Tenant'] = t;
    if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.idempotent) headers['Idempotency-Key'] = newIdempotencyKey();
    return fetch(buildUrl(path, opts.query), { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body), signal: opts.signal });
  };

  if (session.get().tokens && session.get().tokens!.expiresAt - Date.now() < 20_000) await refreshTokens();
  let res = await doFetch();
  if (res.status === 401 && session.get().tokens) {
    if (await refreshTokens()) res = await doFetch();
    else {
      session.set({ tokens: null });
    }
  }
  if (opts.raw) {
    if (!res.ok) throw await toError(res);
    return res as unknown as T;
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) throw await toError(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function toError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: { code: string; message: string; details?: Record<string, unknown> } };
    if (body.error) return new ApiError(res.status, body.error.code, body.error.message, body.error.details ?? {});
  } catch {
    // not JSON
  }
  return new ApiError(res.status, `HTTP_${res.status}`, res.statusText || 'Request failed');
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body: body ?? {} }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body: body ?? {} }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body: body ?? {} }),
  delete: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
  /** Opens a protected file (PDF, image) in a new tab via a blob URL. */
  async open(path: string): Promise<void> {
    const res = await request<Response>('GET', path, { raw: true });
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

/** Request-body type of an operation, straight from the generated contract. */
export type BodyOf<P extends keyof paths, M extends 'post' | 'put' | 'patch'> = paths[P] extends Record<M, { requestBody?: { content: { 'application/json': infer B } } }> ? B : never;

/** Page envelope used by every cursor-paginated list. */
export interface Page<T> {
  data: T[];
  nextCursor: string | null;
}

export function isPage<T>(v: unknown): v is Page<T> {
  return Boolean(v && typeof v === 'object' && Array.isArray((v as Page<T>).data) && 'nextCursor' in (v as object));
}

export function rows<T>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (isPage<T>(v)) return v.data;
  return [];
}
